import _ from 'lodash';

import { useEffect, useState } from 'react';
import {
  Redirect,
  Route,
  RouteComponentProps,
  Switch,
  useHistory,
  useParams,
} from 'react-router-dom';

import * as api from '../../../api';

import AppointmentPage from './AppointmentPage';
import SeriesConfirmationPage from './ConfirmationPage/SeriesConfirmationPage';

import {
  SchedulingFormFieldValue,
  SelectedAppointment,
} from '../../../components/ScheduleAppointment/AppointmentDisplay/common';
import { ScheduleAppointmentPageProps } from '.';
import { parseValue } from 'common/dist/utils/types';

interface RouterProps
  extends ScheduleAppointmentPageProps,
    Pick<RouteComponentProps, 'match' | 'location'> {}

const ScheduleSeriesAppointmentRouter: React.FC<RouterProps> = ({
  formField,
  navigateToNextPage,
  onAppointmentSelected,
  practice,
  encounter,
  match,
  location,
}) => {
  const [selectedAppointments, setSelectedAppointments] = useState<
    (api.EhrTimeSlot.EhrTimeSlot & api.EhrTimeSlot.TimeSlotConfirmation)[]
  >([]);
  const [seriesAppointmentCount, setSeriesAppointmentCount] =
    useState<number>();

  useEffect(() => {
    try {
      const parsedValue = parseValue<SchedulingFormFieldValue>(formField.value);
      if (parsedValue) {
        setSelectedAppointments(parsedValue.selectedAppointments);
        setSeriesAppointmentCount(parsedValue.seriesAppointmentCount);
      } else {
        setSelectedAppointments([]);
        setSeriesAppointmentCount(undefined);
      }
    } catch {
      console.warn(
        'ScheduleSeriesAppointmentRouter could not parse form field value',
      );
    }
  }, [formField.value, formField.id]);

  return (
    <Switch>
      <Redirect
        exact
        from={`${match.path}/`}
        to={{ pathname: `${match.path}/1`, search: location.search }}
      />
      <Route exact path={`${match.path}/confirmation`}>
        <SeriesConfirmationPage
          timeSlotConfirmations={selectedAppointments}
          navigateToNextPage={navigateToNextPage}
          hasBookedAllAppointments={
            selectedAppointments.length === seriesAppointmentCount
          }
        />
      </Route>
      <Route path={`${match.path}/:appointmentNumber`}>
        <ScheduleSeriesAppointment
          formField={formField}
          navigateToNextPage={navigateToNextPage}
          onAppointmentSelected={onAppointmentSelected}
          practice={practice ?? undefined}
          encounter={encounter ?? undefined}
          selectedAppointments={selectedAppointments}
          seriesAppointmentCount={seriesAppointmentCount}
        />
      </Route>
    </Switch>
  );
};

export default ScheduleSeriesAppointmentRouter;

interface Props extends ScheduleAppointmentPageProps {
  selectedAppointments: SelectedAppointment[];
  seriesAppointmentCount: number | undefined;
}

const ScheduleSeriesAppointment: React.FC<Props> = ({
  formField,
  navigateToNextPage,
  onAppointmentSelected,
  practice,
  encounter,
  selectedAppointments,
  seriesAppointmentCount,
}) => {
  const { appointmentNumber } = useParams<{
    appointmentNumber: string;
  }>();
  const history = useHistory();

  const selectedAppointment =
    typeof +appointmentNumber === 'number'
      ? selectedAppointments[+appointmentNumber - 1]
      : undefined;

  const navigateToNextPageHandler = async () => {
    if (selectedAppointments.length !== seriesAppointmentCount) {
      history.push({
        pathname: `./${+appointmentNumber + 1}`,
        search: history.location.search,
      });
    } else {
      navigateToNextPage();
    }
  };

  return (
    <AppointmentPage
      fieldConfig={{
        ..._.pick(formField, [
          'label',
          'helpDialog',
          'scheduleAppointmentDisplayBy',
          'scheduleAppointmentSortBy',
          'scheduleAppointmentStartTime',
          'scheduleAppointmentEndTime',
          'scheduleAppointmentCallSectionEnabled',
          'scheduleAppointmentCommentEnabled',
          'customConfigs',
          'fhirDynamicProviders',
        ]),
        previsitFormFieldId: formField.id,
      }}
      selectedAppointment={selectedAppointment}
      navigateToNextPage={navigateToNextPageHandler}
      onAppointmentSelected={onAppointmentSelected}
      practice={practice}
      encounter={encounter}
      appointmentNumber={+appointmentNumber}
    />
  );
};
